/**
 * Europe/Berlin time math for Focus — pure, Intl-only (no date library).
 *
 * The week grid works in two coordinates: a local *date* (`YYYY-MM-DD`, the
 * column) and a wall-clock *minute of day* (0–1440, the row). Instants are
 * epoch milliseconds. Converting a wall time to an instant goes through
 * `berlinToUtc`, which resolves the two DST edge cases the way Temporal's
 * "compatible" disambiguation does: a time inside the spring-forward gap
 * lands after the gap, a time inside the autumn fold takes the earlier
 * occurrence unless asked for the later one.
 */

export const FOCUS_TZ = "Europe/Berlin";
export const DAY_MINUTES = 1440;
const MINUTE_MS = 60_000;
const DAY_MS = 86_400_000;

interface WallParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const PARTS = new Intl.DateTimeFormat("en-GB", {
  timeZone: FOCUS_TZ,
  hourCycle: "h23",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

const pad = (n: number) => String(n).padStart(2, "0");

/** Wall-clock parts of an instant in Berlin. */
export function berlinParts(ms: number): WallParts {
  const out: WallParts = { year: 0, month: 0, day: 0, hour: 0, minute: 0, second: 0 };
  for (const part of PARTS.formatToParts(new Date(ms))) {
    if (part.type in out) out[part.type as keyof WallParts] = Number(part.value);
  }
  // Some engines still render midnight as "24" under h23.
  if (out.hour === 24) out.hour = 0;
  return out;
}

/** Berlin's UTC offset at an instant, in minutes (+60 CET, +120 CEST). */
export function berlinOffsetMinutes(ms: number): number {
  const whole = Math.floor(ms / 1000) * 1000;
  const p = berlinParts(whole);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return Math.round((asUtc - whole) / MINUTE_MS);
}

function parseDate(date: string): [number, number, number] {
  const [y, m, d] = date.split("-").map(Number);
  return [y, m, d];
}

function utcMidnight(date: string): number {
  const [y, m, d] = parseDate(date);
  return Date.UTC(y, m - 1, d);
}

function formatUtcDate(ms: number): string {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/** The local Berlin date of an instant. */
export function berlinDate(ms: number): string {
  const p = berlinParts(ms);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}

export const todayBerlin = (ms: number) => berlinDate(ms);

/** Wall-clock minutes since local midnight (0–1439). */
export function minuteOfDayBerlin(ms: number): number {
  const p = berlinParts(ms);
  return p.hour * 60 + p.minute;
}

/**
 * The instant at which Berlin's wall clock reads `minuteOfDay` on `date`
 * (1440 = the next midnight). Gap → after the gap; fold → earlier
 * occurrence unless `prefer = "later"`.
 */
export function berlinToUtc(
  date: string,
  minuteOfDay: number,
  prefer: "earlier" | "later" = "earlier",
): number {
  const wall = utcMidnight(date) + minuteOfDay * MINUTE_MS;
  const offsets = new Set([berlinOffsetMinutes(wall - DAY_MS), berlinOffsetMinutes(wall + DAY_MS)]);
  const hits: number[] = [];
  for (const offset of offsets) {
    const instant = wall - offset * MINUTE_MS;
    if (berlinOffsetMinutes(instant) === offset) hits.push(instant);
  }
  if (hits.length === 0) {
    // Spring-forward gap: read the wall time with the offset in force before
    // the change, which lands the same distance past the gap.
    return wall - berlinOffsetMinutes(wall - DAY_MS) * MINUTE_MS;
  }
  hits.sort((a, b) => a - b);
  return prefer === "later" ? hits[hits.length - 1] : hits[0];
}

/** ISO 8601 with Berlin's offset — what the Focus API expects. */
export function toBerlinIso(ms: number): string {
  const p = berlinParts(ms);
  const offset = berlinOffsetMinutes(ms);
  const sign = offset >= 0 ? "+" : "-";
  const abs = Math.abs(offset);
  return (
    `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}` +
    `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`
  );
}

export function addDays(date: string, days: number): string {
  return formatUtcDate(utcMidnight(date) + days * DAY_MS);
}

/** Monday of the week containing `date`. */
export function weekStartOf(date: string): string {
  const dow = new Date(utcMidnight(date)).getUTCDay();
  return addDays(date, -((dow + 6) % 7));
}

/** ISO-8601 week number — the German "KW". */
export function isoWeek(date: string): { year: number; week: number } {
  const t = new Date(utcMidnight(date));
  t.setUTCDate(t.getUTCDate() - ((t.getUTCDay() + 6) % 7) + 3);
  const year = t.getUTCFullYear();
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const days = (t.getTime() - jan4.getTime()) / DAY_MS;
  return { year, week: 1 + Math.round((days - 3 + ((jan4.getUTCDay() + 6) % 7)) / 7) };
}

/**
 * Wall-clock minutes an interval covers on `date`, or null when it misses the
 * day. Clipped to [0, 1440]; an end at the next midnight reads 1440.
 */
export function spanOnDay(
  startMs: number,
  endMs: number,
  date: string,
): { startMin: number; endMin: number } | null {
  const dayStart = berlinToUtc(date, 0);
  const dayEnd = berlinToUtc(addDays(date, 1), 0);
  if (endMs <= dayStart || startMs >= dayEnd) return null;
  const startMin = startMs <= dayStart ? 0 : minuteOfDayBerlin(startMs);
  const endMin = endMs >= dayEnd ? DAY_MINUTES : minuteOfDayBerlin(endMs);
  // The autumn fold can read an end "before" its start; keep a sliver.
  return { startMin, endMin: Math.max(endMin, startMin + 5) };
}

export function snapMinutes(
  minutes: number,
  mode: "round" | "floor" | "ceil" = "round",
  step = 15,
): number {
  return Math[mode](minutes / step) * step;
}

/** "09:15" → 555; null when unparseable. */
export function parseHm(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (h > 24 || m > 59 || (h === 24 && m > 0)) return null;
  return h * 60 + m;
}

/** 555 → "09:15"; 1440 → "00:00" (the next midnight, as a time input reads it). */
export function hmFromMinutes(minutes: number): string {
  const m = ((minutes % DAY_MINUTES) + DAY_MINUTES) % DAY_MINUTES;
  return `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
}

/** "09:00" for an instant, Berlin wall clock. */
export function formatHm(ms: number): string {
  const p = berlinParts(ms);
  return `${pad(p.hour)}:${pad(p.minute)}`;
}

const DAY_LABEL = new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", timeZone: "UTC" });
const DAY_LONG = new Intl.DateTimeFormat("en-GB", {
  weekday: "long",
  day: "numeric",
  month: "long",
  timeZone: "UTC",
});
const MONTH_SHORT = new Intl.DateTimeFormat("en-GB", { month: "short", timeZone: "UTC" });

/** "Mon 12" */
export function formatDayLabel(date: string): string {
  return DAY_LABEL.format(new Date(utcMidnight(date)));
}

/** "Tuesday 13 October" */
export function formatDayLong(date: string): string {
  return DAY_LONG.format(new Date(utcMidnight(date)));
}

/** "12–18 Oct 2026" or "28 Sep – 4 Oct 2026". */
export function formatDateRange(start: string, end: string): string {
  const [sy, , sd] = parseDate(start);
  const [ey, , ed] = parseDate(end);
  const sm = MONTH_SHORT.format(new Date(utcMidnight(start)));
  const em = MONTH_SHORT.format(new Date(utcMidnight(end)));
  if (sy === ey && sm === em) return `${sd}–${ed} ${em} ${ey}`;
  if (sy === ey) return `${sd} ${sm} – ${ed} ${em} ${ey}`;
  return `${sd} ${sm} ${sy} – ${ed} ${em} ${ey}`;
}

/** Countdown text: "18:42", or "1:02:05" from an hour up. */
export function formatClock(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
}

/** 90 → "1h 30m", 240 → "4h", 45 → "45m". */
export function formatMinutes(minutes: number): string {
  const total = Math.round(minutes);
  const sign = total < 0 ? "−" : "";
  const abs = Math.abs(total);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  if (h === 0) return `${sign}${m}m`;
  return m === 0 ? `${sign}${h}h` : `${sign}${h}h ${m}m`;
}

/**
 * A typed duration in minutes: "1:30", "90", "90m", "1.5h", "1,5h", "2h 15m".
 * Null when it can't be read.
 */
export function parseDuration(input: string): number | null {
  const s = input.trim().toLowerCase().replace(",", ".");
  if (!s) return null;
  let match = /^(\d+):(\d{1,2})$/.exec(s);
  if (match) return Number(match[1]) * 60 + Number(match[2]);
  match = /^(\d+(?:\.\d+)?)\s*h$/.exec(s);
  if (match) return Math.round(Number(match[1]) * 60);
  match = /^(\d+)\s*h\s*(\d+)\s*m?$/.exec(s);
  if (match) return Number(match[1]) * 60 + Number(match[2]);
  match = /^(\d+)\s*(m|min)?$/.exec(s);
  if (match) return Number(match[1]);
  return null;
}
